import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View,  StyleSheet, Pressable } from 'react-native';
import { useEditor } from '@grapesjs/react';
import { Text } from '@constants/Themed';
import {
  mdiEyeOffOutline,
  mdiEyeOutline,
  mdiMenuDown,
} from '@mdi/js';
import Icon from '@mdi/react';
import type { Component } from 'grapesjs';
import { cx } from './common';

export interface LayerItemProps {
  component: Component;
  level: number;
  draggingCmp?: Component;
  dragParent?: Component;
}

const itemStyle = { maxWidth: '100%' };

export default function LayerItem({
  component,
  draggingCmp,
  dragParent,
  level,
}: LayerItemProps) {
  const editor = useEditor();
  const { Layers } = editor;
  const layerRef = useRef<View>(null);
  const [layerData, setLayerData] = useState(Layers.getLayerData(component));
  const { open, selected, hovered, components, visible, name } = layerData;
  const componentsIds = components.map((cmp) => cmp.getId());
  const isDragging = draggingCmp === component;
  const cmpHash = componentsIds.join('-');
  const isHovered = hovered || dragParent === component;

  useEffect(() => {
    level === 0 && setLayerData(Layers.getLayerData(component));
    if (layerRef.current) {
      (layerRef.current as any).__cmp = component;
    }
    return () => {
      if (layerRef.current) {
        (layerRef.current as any).__cmp = null;
      }
    }
  }, [component]);

  useEffect(() => {
    const up = (cmp: Component) => {
      cmp === component && setLayerData(Layers.getLayerData(cmp));
    };
    const ev = Layers.events.component;
    editor.on(ev, up);

    return () => {
      editor.off(ev, up);
    };
  }, [editor, Layers, component]);

  const cmpToRender = useMemo(() => {
    return components.map((cmp) => (
      <LayerItem
        key={cmp.getId()}
        component={cmp}
        level={level}
        draggingCmp={draggingCmp}
        dragParent={dragParent}
      />
    ));
  }, [cmpHash, draggingCmp, dragParent]);

  const toggleOpen = () => {
    Layers.setLayerData(component, { open: !open });
  };

  const toggleVisibility = () => {
    Layers.setLayerData(component, { visible: !visible });
  };

  const select = () => {
    Layers.setLayerData(component, { selected: true });
  };

  const hover = (hovered: boolean) => {
    if (!hovered || !draggingCmp) {
      Layers.setLayerData(component, { hovered });
    }
  };

  const wrapperCls = cx(
    styles.layerItem,
    selected && styles.selected,
    (!visible || isDragging) && styles.hiddenOpacity,
  );

  return (
    <View style={wrapperCls}>
      <Pressable
        onPress={select}
        onPressIn={() => hover(true)}
        onPressOut={() => hover(false)}
        style={styles.group}
        data-layer-item
        ref={layerRef}
      >
        <View
          style={cx(
            styles.itemContainer,
            level === 0 && styles.topBorder,
            isHovered && styles.hovered,
            selected && styles.selectedBackground,
          )}
        >
          <Pressable
            style={{ marginLeft: level * 10 }}
            onPress={toggleOpen}
          >
            <Icon
              path={mdiMenuDown}
              size={0.7}
              rotate={open ? 0 : -90}
            />
          </Pressable>
          <Text numberOfLines={1} style={[styles.text, styles.itemStyle]}>
            {name}
          </Text>
          <Pressable
            style={[
              styles.iconContainer,
              visible ? styles.hidden : styles.visible,
            ]}
            onPress={toggleVisibility}
          >
            <Icon
              path={visible ? mdiEyeOutline : mdiEyeOffOutline}
              size={0.7}
            />
          </Pressable>
        </View>
      </Pressable>
      {!!(open && components.length) && (
        <View style={!open && styles.hidden}>{cmpToRender}</View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  layerItem: {
    flexDirection: 'column',
    backgroundColor: '#fff',
    paddingLeft: 10,
  },
  itemStyle: { maxWidth: '100%' },
  selected: {
    backgroundColor: '#1E40AF',
  },
  hiddenOpacity: {
    opacity: 0.5,
  },
  group: {
    maxWidth: '100%',
  },
  itemContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderBottomWidth: 1,
  },
  topBorder: {
    borderTopWidth: 1,
  },
  hovered: {
    backgroundColor: '#4a90e2',
  },
  selectedBackground: {
    backgroundColor: '#1e88e5',
  },
  text: {
    flex: 1,
  },
  iconContainer: {
    opacity: 0,
  },
  visible: {
    opacity: 1,
  },
  hidden: {
    opacity: 0,
  },
});
